import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, CloudArrowUpIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { api, ApiError, type FieldWellnessDraft } from '../lib/api.js';
import { MaskedNameInput, MASKED_UAC_NAME, isProtectedUacName } from '../field-wellness/MaskedNameInput.js';
import { NoteTemplatePicker } from '../field-wellness/NoteTemplatePicker.js';
import { CompletionBadge } from '../field-wellness/CompletionBadge.js';
import { applyUacIdentityPrefill, uacIdentityFields, type WellnessIdentityPrefill } from '../field-wellness/identityPrefill.js';
import { validateNoteTemplates, type NoteTemplate } from '../field-wellness/noteTemplates.js';
import {
  disabledWellnessFields, hiddenWellnessFields, emptyWellnessAnswers, normalizeWellnessAnswers,
  formatNonPiiCaseNumber, MASKED_CASE_OFFICER_NAME, PORTAL_ADDRESS_EXAMPLE,
  requiredWellnessFields, validateWellnessSubmission,
    wellnessFields, wellnessQuestionNumber, wellnessSteps, WELLNESS_FORM_VERSION,
  type WellnessAnswer, type WellnessField, type WellnessContext,
} from '../field-wellness/validation.js';

export function FieldWellnessEditor({ draftId, onClose }: { draftId?: string; onClose?: () => void } = {}) {
  const formVersion = WELLNESS_FORM_VERSION;
  const requiredProtection = 'aag_trident_fips203_204_205_v1';
  const [answers, setAnswers] = useState<WellnessAnswer>(emptyWellnessAnswers);
  const [addressContext, setAddressContext] = useState<WellnessContext>({});
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const [review, setReview] = useState(false);
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [noteTemplates, setNoteTemplates] = useState<NoteTemplate[]>([]);
  const [pendingNotes, setPendingNotes] = useState<Set<string>>(() => new Set());
  const pendingNotesRef = useRef(pendingNotes);
  pendingNotesRef.current = pendingNotes;
  const notePendingChanged = useCallback((field: string, pending: boolean) => {
    setPendingNotes((prior) => {
      if (prior.has(field) === pending) return prior;
      const next = new Set(prior); if (pending) next.add(field); else next.delete(field); return next;
    });
  }, []);
  const [templateError, setTemplateError] = useState(false);
  const [templateReload, setTemplateReload] = useState(0);
  useEffect(() => {
    let current = true;
    void Promise.resolve().then(() => api.wellnessNoteTemplates()).then((library) => {
      const templates = validateNoteTemplates(library.templates);
      if (current) { setNoteTemplates(templates); setTemplateError(false); }
    }).catch(() => { if (current) { setNoteTemplates([]); setTemplateError(true); } });
    return () => { current = false; };
  }, [templateReload]);
  const [caseRecordId, setCaseRecordId] = useState<string | null>(null);
  const [identityPrefill, setIdentityPrefill] = useState<WellnessIdentityPrefill>();
  const [identityConflict, setIdentityConflict] = useState(false);
  const [closing, setClosing] = useState(false);
  const selectedDraftRef = useRef(draftId);
  const conflictRef = useRef(false);
  const retryNeededRef = useRef(false);
  const retryAtRef = useRef(0);
  const retryAttemptRef = useRef(0);
  const dirtySinceRef = useRef<number | null>(null);
  const flushRef = useRef(false);
  const [revision, setRevision] = useState(0);
  const [saveTick, setSaveTick] = useState(0);
  const [syncState, setSyncState] = useState<
    'loading' | 'saving' | 'saved' | 'conflict' | 'error'
  >('loading');
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [protectionActive, setProtectionActive] = useState(false);
  const revisionRef = useRef(0);
  const lastSyncedRef = useRef('');
  const currentSnapshotRef = useRef('');
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const disabled = useMemo(() => disabledWellnessFields(answers), [answers]);
  const hidden = useMemo(() => hiddenWellnessFields(answers), [answers]);
  const required = useMemo(() => requiredWellnessFields(answers), [answers]);
  const validationErrors = useMemo(() => validateWellnessSubmission(answers, addressContext).errors, [answers, addressContext]);
  const isComplete = (name: string) => loaded && !disabled.has(name)
    && Boolean(answers[name]?.trim()) && !validationErrors[name];
  const current = wellnessSteps[step];
  const snapshot = useMemo(
    () => JSON.stringify({ answers, step, review }), [answers, step, review],
  );
  currentSnapshotRef.current = snapshot;
  const displayedSyncState = loaded && syncState === 'saved' && snapshot !== lastSyncedRef.current
    ? 'saving' : syncState;

  const applyDraft = useCallback((draft: FieldWellnessDraft, state: 'saved' | 'conflict') => {
    const verifiedProtection = draft.protectionProfile === requiredProtection;
    const nextContext = {
      originalAddress: draft.originalAddress,
      originalAddressCaseId: draft.originalAddressCaseId,
    };
    const storedAnswers = normalizeWellnessAnswers(draft.answers, nextContext);
    const prefilled = applyUacIdentityPrefill(storedAnswers, verifiedProtection ? draft.identityPrefill : undefined);
    const nextAnswers = prefilled.answers;
    const needsRuleReview = draft.formVersion !== formVersion;
    const nextStep = needsRuleReview
      ? 0 : Math.min(wellnessSteps.length - 1, Math.max(0, draft.step));
    const nextReview = needsRuleReview || prefilled.filled.length > 0 ? false : Boolean(draft.review);
    const nextSnapshot = JSON.stringify({
      answers: nextAnswers, step: nextStep, review: nextReview,
    });
    revisionRef.current = draft.revision;
    selectedDraftRef.current = draft.draftId ?? draftId;
    setCaseRecordId(draft.caseRecordId ?? null);
    setIdentityPrefill(draft.identityPrefill);
    setIdentityConflict(prefilled.conflict);
    conflictRef.current = false;
    retryNeededRef.current = false;
    retryAtRef.current = 0;
    retryAttemptRef.current = 0;
    dirtySinceRef.current = null;
    // Restored blanks filled by the service must be saved, not labeled synced early.
    lastSyncedRef.current = prefilled.filled.length > 0
      ? JSON.stringify({ answers: storedAnswers, step: nextStep, review: Boolean(draft.review) }) : nextSnapshot;
    currentSnapshotRef.current = nextSnapshot;
    setRevision(draft.revision);
    setAnswers(nextAnswers);
    setAddressContext(nextContext);
    setErrors(Object.fromEntries(Object.entries(validateWellnessSubmission(nextAnswers, nextContext).errors)
      .filter(([field]) => uacIdentityFields.some((name) => name === field))));
    setTouched(new Set());
    setStep(nextStep);
    setReview(nextReview);
    setSyncedAt(draft.updatedAt);
    setProtectionActive(verifiedProtection);
    setSyncState(verifiedProtection ? prefilled.filled.length > 0 ? 'saving' : state : 'error');
    setLoaded(verifiedProtection);
  }, [draftId, formVersion, requiredProtection]);

  useEffect(() => {
    mountedRef.current = true;
    void api.fieldWellnessDraft(draftId).then((draft) => {
      if (mountedRef.current) applyDraft(draft, 'saved');
    }).catch(() => {
      if (!mountedRef.current) return;
      const initial = JSON.stringify({ answers: emptyWellnessAnswers(), step: 0, review: false });
      lastSyncedRef.current = initial;
      currentSnapshotRef.current = initial;
      setLoaded(false);
      setSyncState('error');
      setProtectionActive(false);
    });
    return () => { mountedRef.current = false; };
  }, [applyDraft, draftId]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (currentSnapshotRef.current !== lastSyncedRef.current || savingRef.current || pendingNotesRef.current.size) {
        event.preventDefault(); event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    const guardNavigation = (event: Event) => {
      if (currentSnapshotRef.current !== lastSyncedRef.current || savingRef.current || pendingNotesRef.current.size) {
        event.preventDefault();
        setMessage('Your latest answers are not saved yet. Use Save and close before leaving this form.');
      }
    };
    window.addEventListener('veritas:before-navigation', guardNavigation);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('veritas:before-navigation', guardNavigation);
    };
  }, []);

  useEffect(() => {
    if (!closing) return;
    if (syncState === 'error' || syncState === 'conflict') {
      setClosing(false);
      setMessage('This form has not finished saving. Your answers are still here. Reconnect or resolve the saved-copy conflict before closing.');
    } else if (!savingRef.current && snapshot === lastSyncedRef.current) onClose?.();
  }, [closing, snapshot, syncState, revision, onClose]);

  useEffect(() => {
    if (!loaded || reference || snapshot === lastSyncedRef.current) {
      dirtySinceRef.current = null;
      if (loaded && !reference && !savingRef.current && !conflictRef.current) setSyncState('saved');
      return;
    }
    if (conflictRef.current || retryNeededRef.current) return;
    if (navigator.onLine === false) { setSyncState('error'); return; }
    const now = Date.now();
    dirtySinceRef.current ??= now;
    // Quiet typing saves after 900 ms; continuous typing cannot postpone it past 5 seconds.
    const delay = retryAtRef.current > now ? retryAtRef.current - now
      : closing || flushRef.current ? 0 : Math.max(0, Math.min(900, 5_000 - (now - dirtySinceRef.current)));
    if (retryAtRef.current <= now) setSyncState('saving');
    const timer = window.setTimeout(() => {
      if (savingRef.current) return;
      if (navigator.onLine === false) { setSyncState('error'); return; }
      savingRef.current = true;
      flushRef.current = false;
      setSyncState('saving');
      const captured = { answers, step, review };
      const capturedSnapshot = snapshot;
      const baseRevision = revisionRef.current;
      void api.saveFieldWellnessDraft({
        formVersion, ...captured, baseRevision, draftId: selectedDraftRef.current,
      }).then((draft) => {
        if (!mountedRef.current) return;
        if (draft.protectionProfile !== requiredProtection || !Number.isInteger(draft.revision)
            || draft.revision <= baseRevision
            || (selectedDraftRef.current && draft.draftId !== selectedDraftRef.current)) {
          throw new Error('The protected draft save could not be verified.');
        }
        retryAtRef.current = 0;
        retryAttemptRef.current = 0;
        dirtySinceRef.current = null;
        revisionRef.current = draft.revision;
        selectedDraftRef.current = draft.draftId ?? selectedDraftRef.current;
        lastSyncedRef.current = capturedSnapshot;
        setRevision(draft.revision);
        const nextContext = { originalAddress: draft.originalAddress,
          originalAddressCaseId: draft.originalAddressCaseId };
        setAddressContext(nextContext);
        // A late response must not restore an old case or overwrite user edits.
        setAnswers((currentAnswers) => currentAnswers.nonPliCase === draft.originalAddressCaseId
          && currentAnswers.locatedAtAddress === 'Yes'
          ? { ...currentAnswers, locationComments: draft.originalAddress ?? '' }
          : currentAnswers);
        setSyncedAt(draft.updatedAt);
        setSyncState(currentSnapshotRef.current === capturedSnapshot ? 'saved' : 'saving');
      }).catch(async (cause) => {
        if (!mountedRef.current) return;
        const conflict = (cause instanceof ApiError && cause.reason === 'field_wellness_draft_changed')
          || (cause instanceof Error && cause.message === 'field_wellness_draft_changed');
        if (!conflict) {
          retryNeededRef.current = !isRetryableDraftError(cause);
          retryAtRef.current = retryNeededRef.current ? 0
            : Date.now() + Math.min(30_000, 3_000 * 2 ** Math.min(retryAttemptRef.current++, 4));
          setSyncState('error');
          return;
        }
        conflictRef.current = true;
        setSyncState('conflict');
        setMessage('Another device changed this form. Your unsaved answers are still here. Load the saved copy only when you are ready to replace them.');
      }).finally(() => {
        savingRef.current = false;
        if (mountedRef.current && currentSnapshotRef.current !== lastSyncedRef.current) {
          setSaveTick((value) => value + 1);
        }
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [answers, closing, formVersion, loaded, reference, requiredProtection, review, revision, saveTick, snapshot, step]);

  useEffect(() => {
    const resume = () => {
      if (conflictRef.current || retryNeededRef.current) return;
      retryAtRef.current = 0;
      flushRef.current = true;
      setSaveTick((tick) => tick + 1);
    };
    const visibility = () => {
      // Start pending work before backgrounding when possible, and resume on return.
      // The OS may suspend networking, so only a server acknowledgement means saved.
      if (currentSnapshotRef.current !== lastSyncedRef.current) resume();
    };
    const offline = () => {
      if (currentSnapshotRef.current !== lastSyncedRef.current) setSyncState('error');
    };
    window.addEventListener('online', resume);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  useEffect(() => {
    if (!loaded || reference) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible' || savingRef.current
          || pendingNotesRef.current.size || currentSnapshotRef.current !== lastSyncedRef.current) return;
      const requestedSnapshot = currentSnapshotRef.current;
      void api.fieldWellnessDraft(selectedDraftRef.current).then((draft) => {
        if (!mountedRef.current || savingRef.current || pendingNotesRef.current.size
            || currentSnapshotRef.current !== requestedSnapshot
            || currentSnapshotRef.current !== lastSyncedRef.current) return;
        if (draft.revision > revisionRef.current) applyDraft(draft, 'saved');
        else if (draft.revision === revisionRef.current && draft.protectionProfile === requiredProtection) setSyncState('saved');
      }).catch(() => {
        if (mountedRef.current && !savingRef.current && currentSnapshotRef.current === requestedSnapshot
            && currentSnapshotRef.current === lastSyncedRef.current) setSyncState('error');
      });
    };
    const timer = window.setInterval(refresh, 30_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [applyDraft, loaded, reference]);

  function update(name: string, value: string) {
    if (!loaded || closing || disabled.has(name) || (name === 'nonPliCase' && caseRecordId)) return;
    const source = { ...answers, [name]: value };
    if (name === 'locatedAtAddress' && value === 'No' && answers.locatedAtAddress === 'Yes') {
      source.locationComments = '';
    }
    const next = normalizeWellnessAnswers(source, addressContext);
    // Preserve spaces during entry; normalize when validating or saving.
    if (isProtectedUacName(name)) next[name] = value.normalize('NFKC').slice(0, 100);
    // Do not trim the just-entered newline or space out from under the cursor.
    // Canonical trimming still runs during validation and server persistence.
    if (wellnessFields.some((field) => field.name === name && ['textarea', 'address'].includes(field.kind))) {
      next[name] = value.normalize('NFKC').slice(0, 2_000);
    }
    const result = validateWellnessSubmission(next, addressContext);
    const nextTouched = new Set(touched);
    nextTouched.add(name);
    if (name === 'vendorContact' || name === 'returnInfo') {
      nextTouched.add('vendorContact');
      nextTouched.add('returnInfo');
    }
    if (name === 'locatedAtAddress') {
      nextTouched.add(next.locatedAtAddress === 'No' ? 'locationComments' : 'safeAtVisit');
    }
    setAnswers(next);
    if (uacIdentityFields.some((field) => field === name)) setIdentityConflict(false);
    setMessage(null);
    setTouched(nextTouched);
    setErrors(Object.fromEntries(
      Object.entries(result.errors).filter(([fieldName]) => nextTouched.has(fieldName)),
    ));
  }

  function continueForm() {
    if (pendingNotes.size) { setMessage('Insert or cancel the standard wording details before continuing.'); return; }
    const result = validateWellnessSubmission(answers, addressContext);
    setMessage(null);
    setAnswers(result.data);
    const stepErrors = Object.fromEntries(
      Object.entries(result.errors).filter(([name]) => current.fields.some((field) => field.name === name)),
    );
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      document.getElementById(`wellness-${Object.keys(stepErrors)[0]}`)?.focus();
      return;
    }
    setErrors({});
    const terminalOutcome = result.data.vendorContact === 'Yes' || result.data.returnInfo === 'Yes';
    if (step === 1 && terminalOutcome && result.valid) setReview(true);
    else if (step < wellnessSteps.length - 1) setStep((value) => value + 1);
    else if (result.valid) setReview(true);
    else {
      const first = Object.keys(result.errors)[0];
      const target = wellnessSteps.findIndex((section) => section.fields.some((field) => field.name === first));
      setStep(Math.max(0, target));
      setErrors(result.errors);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit() {
    const result = validateWellnessSubmission(answers, addressContext);
    if (!result.valid) {
      const first = Object.keys(result.errors)[0];
      const target = wellnessSteps.findIndex((section) => section.fields.some((field) => field.name === first));
      setTouched(new Set(Object.keys(result.errors)));
      setErrors(result.errors);
      setReview(false);
      setStep(Math.max(0, target));
      setMessage('Correct the highlighted answers before submitting.');
      return;
    }
    if (!attested || submitting || closing || !loaded || conflictRef.current || syncState !== 'saved') return;
    if (snapshot !== lastSyncedRef.current || savingRef.current) {
      setMessage('Wait for the protected draft to finish synchronizing, then submit.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const saved = await api.submitFieldWellness({
        formVersion, answers: result.data, attested: true,
        draftId: selectedDraftRef.current, baseRevision: revisionRef.current,
      });
      setReference(saved.reference);
      setAnswers(emptyWellnessAnswers());
      setAddressContext({});
      setTouched(new Set());
      setStep(0);
      setReview(false);
      setAttested(false);
      revisionRef.current = 0;
      setRevision(0);
      const cleared = JSON.stringify({ answers: emptyWellnessAnswers(), step: 0, review: false });
      lastSyncedRef.current = cleared;
      currentSnapshotRef.current = cleared;
      setSyncState('saved');
      setSyncedAt(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.reason === 'field_wellness_draft_changed') {
        conflictRef.current = true;
        setSyncState('conflict');
        setMessage('Another device changed this form. Review the saved copy before submitting; your current answers are still here.');
      } else setMessage('The wellness record could not be submitted. Your draft is still saved. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const draftControls = <>
    {onClose && !loaded && <button type="button" className="wellness-nav-button mb-4" onClick={onClose}><ChevronLeftIcon aria-hidden /> Back to cases</button>}
    {onClose && <button type="button" className="wellness-nav-button mb-4" disabled={!loaded || closing || submitting || pendingNotes.size > 0}
      onClick={() => {
        retryNeededRef.current = false;
        retryAtRef.current = 0;
        if (!conflictRef.current) setSyncState(snapshot === lastSyncedRef.current ? 'saved' : 'saving');
        setClosing(true); setSaveTick((tick) => tick + 1);
      }}>
      <CloudArrowUpIcon aria-hidden />
      {closing ? 'Saving before closing…' : 'Save and close'}
    </button>}
    {syncState === 'error' && loaded && <button type="button" className="btn btn-secondary my-3"
      onClick={() => { retryNeededRef.current = false; retryAtRef.current = 0; flushRef.current = true;
        setSyncState('saving'); setSaveTick((tick) => tick + 1); }}>
      Try saving again
    </button>}
    {syncState === 'conflict' && <button type="button" className="btn btn-secondary my-3" onClick={() => {
      if (!window.confirm('Replace the unsaved answers on this screen with the saved copy from the server?')) return;
      void api.fieldWellnessDraft(selectedDraftRef.current).then((draft) => { applyDraft(draft, 'saved'); setMessage(null); })
        .catch(() => setMessage('The saved copy could not be loaded. Your answers have not been replaced.'));
    }}>Load saved copy</button>}
  </>;

  if (reference) return <section className="border border-presence-active bg-neutral-100 p-5 text-center" role="status">
    <CheckCircleIcon className="mx-auto h-12 w-12 text-presence-active" aria-hidden />
    <h2 className="mt-3 text-h3">Record submitted</h2>
    <p className="mt-2 text-neutral-700">Reference <strong>{reference}</strong></p>
    <button type="button" className="wellness-nav-button wellness-nav-button--forward mt-5" onClick={() => {
      if (onClose) { onClose(); return; }
      setReference(null); setStep(0); setReview(false); setErrors({});
      setTouched(new Set());
    }}>{onClose ? 'Back to cases' : 'Start another record'}</button>
  </section>;

  if (review) return <form className="wellness-form" aria-labelledby="wellness-review-title"
    onSubmit={(event) => { event.preventDefault(); void submit(); }}>
    {draftControls}
    <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Protected field record</p>
    <h2 id="wellness-review-title" className="text-h3 mt-1">Review and submit</h2>
    <ProtectionStatus active={protectionActive} />
    <p className="mt-2 text-sm text-neutral-700">Answers are protected in transit and stored only as encrypted records. UAC names are held behind random vault tokens.</p>
    <SyncStatus state={displayedSyncState} updatedAt={syncedAt} retrying={!retryNeededRef.current} pendingNotes={pendingNotes.size > 0} />
    {message && <p className="form-error mt-3" role="alert">{message}</p>}
    <dl className="mt-4 divide-y divide-neutral-300 border-y border-neutral-300 bg-neutral-100 px-3">
      {wellnessSteps.flatMap((section) => section.fields).filter((field) => !hidden.has(field.name)).map((field) => <div key={field.name} className="py-3">
        <dt className="text-xs text-neutral-700">Question {wellnessQuestionNumber(field)}. {field.label}
          <CompletionBadge complete={isComplete(field.name)} required={required.has(field.name) && !disabled.has(field.name)} />
        </dt>
        <dd className={`mt-1 break-words font-semibold ${
          answers[field.name] ? '' : 'text-alert-700'
        }`}>{isProtectedUacName(field.name) && answers[field.name]
          ? MASKED_UAC_NAME : field.kind === 'officer' && answers[field.name]
          ? MASKED_CASE_OFFICER_NAME : answers[field.name] || 'Blank by rule'}</dd>
      </div>)}
    </dl>
    <label className="mt-4 flex items-start gap-3 border border-neutral-300 bg-neutral-100 p-4">
      <input type="checkbox" className="mt-1 h-5 w-5" checked={attested}
        onChange={(event) => setAttested(event.target.checked)} />
      <span>I confirm this record is accurate to the best of my knowledge.</span>
    </label>
    <nav className="wellness-navigation" aria-label="Form navigation">
      <button type="button" className="wellness-nav-button" disabled={closing || submitting}
        onClick={() => setReview(false)}><ChevronLeftIcon className="h-5 w-5" /> Edit</button>
      <button type="submit" className="wellness-nav-button wellness-nav-button--forward"
        disabled={!loaded || !attested || closing || submitting || displayedSyncState !== 'saved'}><CheckCircleIcon className="h-5 w-5" />
        {submitting ? 'Submitting…' : 'Submit'}</button>
    </nav>
  </form>;

  return <form className="wellness-form" aria-labelledby="wellness-form-title"
    onSubmit={(event) => { event.preventDefault(); if (loaded && !closing) continueForm(); }}>
    {draftControls}
    <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Field wellness</p>
    <h2 id="wellness-form-title" className="text-h3 mt-1">{current.title}</h2>
    <p className="mt-1 text-xs text-neutral-700">Green checks mean the answer meets the form rules. Saving is shown separately below.</p>
    <ProtectionStatus active={protectionActive} />
    <SyncStatus state={displayedSyncState} updatedAt={syncedAt} retrying={loaded && !retryNeededRef.current} pendingNotes={pendingNotes.size > 0} />
    {loaded && step === 0 && <div className="mt-3 border border-neutral-300 bg-white p-3 text-sm" role="status">
      <strong>{uacIdentityFields.every((field) => answers[field]?.trim())
        ? 'UAC details are filled. Names remain hidden.' : 'UAC details need attention.'}</strong>
      <p className="mt-1">{identityConflict
        ? 'Saved answers differ from the available case information. Nothing was replaced. Confirm the details before continuing.'
        : identityPrefill?.source === 'unavailable'
          ? 'Saved case information could not be verified. Enter confirmed details or ask an administrator to check the record.'
          : identityPrefill?.source === 'saved_form'
            ? 'Available identity details come from your previous form for this case. Confirm they still apply.'
            : identityPrefill?.source === 'case_import'
              ? 'Available identity details come from the linked case import. Your existing answers are preserved.'
              : 'Saved details are reused when available. Enter any missing information from the case record.'}</p>
      {uacIdentityFields.some((field) => !answers[field]?.trim()) && <p className="mt-1 font-semibold text-alert-700">
        Missing: {wellnessFields.filter((field) => uacIdentityFields.some((name) => name === field.name)
          && !answers[field.name]?.trim()).map((field) => field.label).join(', ')}. Required details cannot be skipped.
      </p>}
    </div>}
    {message && <p className="form-error mt-3" role="alert">{message}</p>}
    {loaded && (!answers.officerFirstName || !answers.officerLastName) && <p className="form-error mt-3" role="alert">
      An administrator must configure the shared case officer before this record can be submitted.
      No action is needed in the field.
    </p>}
    <div className="mt-3 grid grid-cols-5 gap-1" aria-hidden="true">
      {wellnessSteps.map((section, index) => <span key={section.title}
        className={`h-2 rounded-full ${index <= step ? 'bg-accent-700' : 'bg-neutral-300'}`} />)}
    </div>
    <div className="mt-5 space-y-5">
      {current.fields.some((field) => field.kind === 'textarea' && !hidden.has(field.name)) && templateError && <div className="text-sm" role="status">
        Standard wording is unavailable. You can still enter your note.
        <button type="button" className="btn btn-secondary mt-2" onClick={() => setTemplateReload((value) => value + 1)}>Reload wording</button>
      </div>}
      {current.fields.filter((field) => !hidden.has(field.name)).map((field) => <WellnessInput key={field.name} field={field}
        noteTemplates={noteTemplates}
        onPendingChange={notePendingChanged}
        value={answers[field.name]} disabled={!loaded || closing || disabled.has(field.name)}
        readOnly={field.name === 'nonPliCase' && Boolean(caseRecordId)}
        required={required.has(field.name)} error={errors[field.name]}
        complete={isComplete(field.name)}
        stoppedAtContact={disabled.has(field.name)}
        onChange={(value) => update(field.name, value)} />)}
    </div>
    {step === 1 && <PortalPathStatus answers={answers} />}
    <nav className="wellness-navigation" aria-label="Form navigation">
      <button type="button" className="wellness-nav-button" disabled={!loaded || step === 0 || closing || pendingNotes.size > 0}
        onClick={() => { setErrors({}); setStep((value) => Math.max(0, value - 1)); }}>
        <ChevronLeftIcon className="h-5 w-5" /> Back
      </button>
      <button type="submit" className="wellness-nav-button wellness-nav-button--forward" disabled={!loaded || closing}>
        {step === wellnessSteps.length - 1 ? 'Review' : 'Continue'}
        <ChevronRightIcon className="h-5 w-5" />
      </button>
    </nav>
  </form>;
}

function ProtectionStatus({ active }: { active: boolean }) {
  return <div className={`mt-3 flex items-center gap-2 border px-3 py-2 text-sm ${
    active
      ? 'border-presence-active bg-neutral-100 text-neutral-800'
      : 'border-alert-300 bg-alert-100 text-alert-800'
  }`} role="status" aria-live="polite">
    <ShieldCheckIcon className="h-5 w-5 shrink-0" aria-hidden />
    <span><strong>{active
      ? 'AAG 3D Triple Lattice protection verified'
      : 'Protected form channel is not verified'}</strong>
      <span className="block text-xs">{active
        ? 'UAC names are tokenized; FIPS 203, 204, and 205 profile storage is active.'
        : 'Entry is locked until the protected service reconnects.'}</span>
    </span>
  </div>;
}

function isRetryableDraftError(cause: unknown): boolean {
  if (cause instanceof ApiError) return cause.status === 408 || cause.status === 429 || cause.status >= 500;
  if (!(cause instanceof Error)) return false;
  // Native transport errors are plain Errors. Do not retry authentication, validation,
  // revision conflicts, or failed cryptographic verification automatically.
  return /^(?:mobile_gateway_http_|HTTP_)(?:408|429|5\d\d)$/u.test(cause.message)
    || /^(?:offline|internal_error|network_error|request_timeout)$/iu.test(cause.message)
    || /failed to fetch|network request failed|networkerror|timed out|unable to resolve host|connection reset/iu.test(cause.message);
}

function SyncStatus({ state, updatedAt, retrying, pendingNotes }: {
  state: 'loading' | 'saving' | 'saved' | 'conflict' | 'error';
  updatedAt: string | null;
  retrying: boolean;
  pendingNotes: boolean;
}) {
  const label = state === 'loading' ? 'Loading synchronized draft'
    : state === 'saving' ? 'Saving securely'
    : state === 'conflict' ? 'Saved elsewhere; review needed'
    : state === 'error' ? 'Sync needs a connection'
    : 'Synced across devices';
  return <div className={`mt-3 flex items-center gap-2 border px-3 py-2 text-sm ${
    state === 'error' ? 'border-alert-300 bg-alert-100 text-alert-800'
      : 'border-neutral-300 bg-neutral-100 text-neutral-700'
  }`} role="status">
    <CloudArrowUpIcon className="h-5 w-5 shrink-0" aria-hidden />
    <span><strong>{label}</strong>{updatedAt && state === 'saved'
      ? ` · ${new Date(updatedAt).toLocaleString()}` : ''}
      <span className="block">{state === 'error'
        ? retrying ? 'Changes are not saved yet. Keep this form open; autosave will retry when connected.'
          : 'Saving needs attention. Keep this form open and try again before leaving.'
        : state === 'saving' ? 'Changes save automatically. Please keep this form open until saving finishes.'
        : state === 'saved' ? 'Answers save automatically as you work.' : ''}</span>
      {pendingNotes && <span className="block text-alert-700">Wording details are not saved until you insert them into the note.</span>}
    </span>
  </div>;
}

function WellnessInput({ field, value, disabled, required, complete, error, onChange, stoppedAtContact, readOnly = false, noteTemplates, onPendingChange }: {
  field: WellnessField; value: string; disabled: boolean; required: boolean; error?: string;
  complete: boolean;
  onChange: (value: string) => void;
  stoppedAtContact: boolean;
  readOnly?: boolean;
  noteTemplates: NoteTemplate[];
  onPendingChange: (field: string, pending: boolean) => void;
}) {
  const id = `wellness-${field.name}`;
  const questionNumber = wellnessQuestionNumber(field);
  const derived = Boolean(field.derivedFrom);
  const accessibleLabel = `Question ${questionNumber}. ${field.label}`;
  return <div data-wellness-field={field.name} data-complete={complete} className={complete ? 'wellness-answer-complete' : undefined}>
    <label className="field" htmlFor={id}><span><strong className="mr-2 text-accent-800">{questionNumber}.</strong>{field.label}
      {derived
        ? <strong className="ml-2 text-accent-700">Automatic</strong>
        : <CompletionBadge id={`${id}-completion`} complete={complete} required={required && !disabled} />}
    </span></label>
    {field.kind === 'choice' ? <div id={id} className="grid grid-cols-2 gap-2" role="radiogroup"
      tabIndex={-1} aria-required={required} aria-invalid={Boolean(error)} aria-disabled={disabled}
      aria-label={accessibleLabel} aria-describedby={`${id}-completion${error ? ` ${id}-error` : ''}`}>
      {field.allowedValues?.map((option) => <button key={option} type="button" role="radio"
        aria-checked={value === option} disabled={disabled}
        className={`btn ${value === option ? 'btn-primary' : 'btn-secondary'}`}
        onClick={() => onChange(option)}>{option}</button>)}
    </div> : field.kind === 'textarea' ? <><textarea id={id} className="input min-h-[104px]"
      value={value} maxLength={2000} disabled={disabled} aria-invalid={Boolean(error)}
      aria-required={required}
      aria-describedby={error ? `${id}-error` : undefined}
      onChange={(event) => onChange(event.target.value)} />
      <NoteTemplatePicker field={field.name} value={value} templates={noteTemplates} disabled={disabled || readOnly}
        onChange={onChange} onPendingChange={onPendingChange} />
      </> : field.kind === 'address' ? <div>
      <input id={id} className="input" value={value} maxLength={200} disabled={disabled}
        autoComplete="street-address" placeholder={PORTAL_ADDRESS_EXAMPLE}
        aria-required={required} aria-invalid={Boolean(error)}
        aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
        onChange={(event) => onChange(event.target.value)} />
      <p id={`${id}-help`} className="mt-1 text-xs text-neutral-700">
        {field.name === 'confirmedAddress' ? 'Optional. ' : ''}
        Use {PORTAL_ADDRESS_EXAMPLE}. ZIP+4 is accepted.
      </p>
    </div> : field.kind === 'case' ? <div>
      <div className="flex">
        <span className="flex min-h-touch items-center border border-r-0 border-neutral-400 bg-neutral-200 px-3 font-semibold"
          aria-hidden>14-</span>
        <input id={id} className="input rounded-l-none" value={value.startsWith('14-') ? value.slice(3) : ''}
          maxLength={5} inputMode="numeric" autoComplete="off" disabled={disabled}
          readOnly={readOnly} aria-readonly={readOnly}
          aria-required={required}
          aria-invalid={Boolean(error)} aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
          onChange={(event) => onChange(formatNonPiiCaseNumber(event.target.value))} />
      </div>
      <p id={`${id}-help`} className="mt-1 text-xs text-neutral-700">
        {readOnly ? 'Selected case. Return to the case list to work on a different record.' : 'Enter the five digits following the fixed 14- prefix.'}
      </p>
    </div> : field.kind === 'officer' ? <div>
      <input id={id} className="input bg-neutral-100" readOnly disabled={disabled}
        value={value ? MASKED_CASE_OFFICER_NAME : 'Not configured'}
        aria-required={required}
        aria-invalid={Boolean(error)} aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`} />
      <p id={`${id}-help`} className="mt-1 text-xs text-neutral-700">
        Selected centrally by an administrator and hidden from field devices.
      </p>
    </div> : isProtectedUacName(field.name) ? <MaskedNameInput
      id={id} value={value} disabled={disabled} required={required} error={error}
      onChange={onChange} /> : derived ? <div>
      <input id={id} className="input bg-neutral-100" value={value} readOnly
        inputMode="numeric" autoComplete="off" aria-readonly="true"
        aria-label={accessibleLabel} aria-required={required} aria-invalid={Boolean(error)}
        aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`} />
      <p id={`${id}-help`} className="mt-1 text-xs text-neutral-700">
        Filled automatically from Question {wellnessQuestionNumber(field.derivedFrom ?? '')}.
        No second entry is needed.
      </p>
    </div> : <input id={id} className="input"
      value={value} maxLength={field.kind === 'alien' ? 9 : field.kind === 'name' ? 100 : 2000}
      inputMode={field.kind === 'alien' ? 'numeric' : 'text'} disabled={disabled}
      autoComplete="off" autoCapitalize={field.kind === 'name' ? 'words' : undefined}
      aria-required={required} aria-invalid={Boolean(error)}
      aria-describedby={field.kind === 'alien'
        ? `${id}-help${error ? ` ${id}-error` : ''}`
        : error ? `${id}-error` : undefined}
      onChange={(event) => onChange(event.target.value)} />}
    {field.kind === 'alien' && !derived && <p id={`${id}-help`} className="mt-1 text-xs text-neutral-700">
      {value.length}/9 digits. Letters and punctuation are removed automatically.
    </p>}
    {disabled && <p className={`mt-1 text-sm font-semibold ${stoppedAtContact ? 'text-alert-700' : 'text-neutral-700'}`}>
      {stoppedAtContact ? <>
      Not required because the visit stopped at contact.
      </> : 'Read-only until the protected draft finishes loading.'}
    </p>}
    {error && <p id={`${id}-error`} className="field-error mt-1" role="alert">{error}</p>}
  </div>;
}

function PortalPathStatus({ answers }: { answers: WellnessAnswer }) {
  const conflict = answers.vendorContact === 'Yes' && answers.returnInfo === 'Yes';
  const stopped = !conflict && (answers.vendorContact === 'Yes' || answers.returnInfo === 'Yes');
  const fullVisit = answers.vendorContact === 'No' && answers.returnInfo === 'No';
  const tone = conflict ? 'border-alert-300 bg-alert-100 text-alert-800'
    : stopped || fullVisit ? 'border-presence-active bg-neutral-100 text-neutral-800'
      : 'border-neutral-300 bg-neutral-100 text-neutral-700';
  const message = conflict
    ? 'These answers cannot both be Yes. Change one before continuing.'
    : stopped
      ? 'Portal path confirmed. The remaining visit questions are not required.'
      : fullVisit
        ? 'Full visit path confirmed. Every applicable Yes or No question will be required.'
        : 'Answer both questions, or choose Yes for one outcome, to establish the portal path.';
  return <div className={`mt-4 border p-3 text-sm ${tone}`} role="status" aria-live="polite">
    <strong>{message}</strong>
  </div>;
}
